// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const REVIEW_PLATFORMS = [
  { id: 'google', name: 'Google Reviews', icon: '\uD83D\uDD0D', rating: 4.7, count: 124, connected: true },
  { id: 'yelp', name: 'Yelp', icon: '\u2B50', rating: 4.3, count: 47, connected: true },
  { id: 'facebook', name: 'Facebook Reviews', icon: '\uD83D\uDCD8', rating: 4.8, count: 89, connected: true },
  { id: 'tripadvisor', name: 'TripAdvisor', icon: '\uD83E\uDDED', rating: 0, count: 0, connected: false },
  { id: 'bbb', name: 'Better Business Bureau', icon: '\uD83C\uDFC6', rating: 0, count: 0, connected: false },
];

const DEMO_REVIEWS = [
  { id: 1, platform: 'google', author: 'Michael R.', rating: 5, text: 'Absolutely incredible service! The team went above and beyond. Would highly recommend to anyone.', date: 'Feb 18, 2026', responded: true, sentiment: 'positive' },
  { id: 2, platform: 'yelp', author: 'Jennifer L.', rating: 4, text: 'Great experience overall. The product quality is top-notch, delivery was a bit slow though.', date: 'Feb 16, 2026', responded: false, sentiment: 'positive' },
  { id: 3, platform: 'facebook', author: 'Dave K.', rating: 5, text: 'Best in the business! Professional, reliable, and their customer service is unmatched.', date: 'Feb 15, 2026', responded: true, sentiment: 'positive' },
  { id: 4, platform: 'google', author: 'Sarah T.', rating: 2, text: 'Had some issues with my order. The product was fine but communication could be better.', date: 'Feb 14, 2026', responded: false, sentiment: 'negative' },
  { id: 5, platform: 'facebook', author: 'Chris M.', rating: 3, text: 'Decent service. Nothing special but nothing wrong either. Average experience.', date: 'Feb 12, 2026', responded: false, sentiment: 'neutral' },
];

const RESPONSE_TEMPLATES = [
  { id: 1, type: 'positive', name: 'Thank You (5-star)', text: 'Thank you so much for your wonderful review, {name}! We\'re thrilled to hear about your positive experience. We look forward to serving you again!' },
  { id: 2, type: 'positive', name: 'Appreciation (4-star)', text: 'Thank you for your feedback, {name}! We\'re glad you had a great experience. We\'re always working to improve and your input helps us do that.' },
  { id: 3, type: 'negative', name: 'Apology & Resolution', text: 'We\'re sorry to hear about your experience, {name}. This isn\'t the standard we hold ourselves to. Please reach out to us directly so we can make this right.' },
  { id: 4, type: 'neutral', name: 'Encourage Return', text: 'Thank you for your honest feedback, {name}. We appreciate you taking the time to share your experience. We\'d love the opportunity to exceed your expectations next time.' },
];

function ReviewManagerPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('feed');
  const totalReviews = REVIEW_PLATFORMS.reduce((sum, p) => sum + p.count, 0);
  const avgRating = REVIEW_PLATFORMS.filter(p => p.count > 0).reduce((sum, p) => sum + p.rating * p.count, 0) / totalReviews;
  const positiveCount = DEMO_REVIEWS.filter(r => r.sentiment === 'positive').length;
  const respondedCount = DEMO_REVIEWS.filter(r => r.responded).length;

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} style={{ color: i < Math.round(rating) ? '#f59e0b' : 'var(--border)', fontSize: 16 }}>{'\u2605'}</span>
    ));
  };

  const stats = [
    { label: 'Overall Rating', value: avgRating.toFixed(1) + '/5', color: '#f59e0b' },
    { label: 'Total Reviews', value: totalReviews.toString(), color: '#6366f1' },
    { label: 'Response Rate', value: Math.round(respondedCount / DEMO_REVIEWS.length * 100) + '%', color: '#10b981' },
    { label: 'Positive', value: Math.round(positiveCount / DEMO_REVIEWS.length * 100) + '%', color: '#3b82f6' },
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\u2B50'} Reviews & Reputation</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>Monitor and manage your online reputation across all platforms.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{s.label}</p>
            <p style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['feed', 'platforms', 'request', 'templates'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 20px', background: tab === t ? '#6366f1' : 'var(--card)', color: tab === t ? '#fff' : 'var(--text-muted)', border: tab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t === 'request' ? 'Request Reviews' : t}</button>
        ))}
      </div>
      {tab === 'feed' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {DEMO_REVIEWS.map(r => {
            const plat = REVIEW_PLATFORMS.find(p => p.id === r.platform);
            return (
              <div key={r.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ fontSize: 18 }}>{plat?.icon}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{r.author}</span>
                  <div style={{ display: 'flex' }}>{renderStars(r.rating)}</div>
                  <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, background: r.sentiment === 'positive' ? 'rgba(16,185,129,0.1)' : r.sentiment === 'negative' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)', color: r.sentiment === 'positive' ? '#10b981' : r.sentiment === 'negative' ? '#ef4444' : '#f59e0b' }}>{r.sentiment}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{r.date}</span>
                </div>
                <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, marginBottom: 10 }}>{r.text}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {r.responded ? (
                    <span style={{ fontSize: 12, color: '#10b981', fontWeight: 500 }}>{'\u2713'} Responded</span>
                  ) : (
                    <>
                      <button style={{ padding: '6px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Reply</button>
                      <button style={{ padding: '6px 14px', background: 'rgba(99,102,241,0.1)', color: '#6366f1', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>{'\u2728'} AI Reply</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {tab === 'platforms' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {REVIEW_PLATFORMS.map(p => (
            <div key={p.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 28 }}>{p.icon}</span>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{p.name}</h3>
              </div>
              {p.connected ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>{p.rating}</span>
                    <div>{renderStars(p.rating)}</div>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{p.count} reviews</p>
                </div>
              ) : (
                <button style={{ padding: '8px 16px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Connect</button>
              )}
            </div>
          ))}
        </div>
      )}
      {tab === 'request' && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Request Reviews from Customers</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div><label style={labelStyle}>Customer Name</label><input type="text" placeholder="John Smith" style={inputStyle} /></div>
            <div><label style={labelStyle}>Email or Phone</label><input type="text" placeholder="john@example.com or (403) 555-0123" style={inputStyle} /></div>
          </div>
          <div style={{ marginBottom: 16 }}><label style={labelStyle}>Message Template</label>
            <textarea rows={4} style={{ ...inputStyle, resize: 'vertical' }} defaultValue="Hi {name}, thank you for choosing us! We'd love to hear about your experience. Would you mind leaving us a quick review? It only takes 30 seconds and helps us serve you better." />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ padding: '10px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Send via Email</button>
            <button style={{ padding: '10px 20px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Send via SMS</button>
          </div>
        </div>
      )}
      {tab === 'templates' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {RESPONSE_TEMPLATES.map(t => (
            <div key={t.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, background: t.type === 'positive' ? 'rgba(16,185,129,0.1)' : t.type === 'negative' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)', color: t.type === 'positive' ? '#10b981' : t.type === 'negative' ? '#ef4444' : '#f59e0b' }}>{t.type}</span>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{t.name}</h4>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{t.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default ReviewManagerPage;
